// School Email Domain Validation Service
// Validates if an email domain belongs to an educational institution

// Common educational domains
const EDUCATIONAL_DOMAINS = new Set([
  // US Educational domains
  '.edu',
  '.k12.us',

  // International educational domains
  '.ac.uk',    // UK universities
  '.edu.au',   // Australian universities
  '.ac.za',    // South African universities
  '.edu.ca',   // Canadian educational
  '.ac.nz',    // New Zealand universities
  '.edu.sg',   // Singapore educational
  '.ac.in',    // Indian universities
  '.edu.my',   // Malaysian educational

  // Middle East & GCC educational domains
  '.edu.qa',   // Qatar educational
  '.edu.ae',   // UAE educational
  '.edu.sa',   // Saudi Arabia educational
  '.edu.kw',   // Kuwait educational
  '.edu.bh',   // Bahrain educational
  '.edu.om',   // Oman educational
  '.ac.ae',    // UAE universities
  '.ac.qa',    // Qatar universities
  '.edu.jo',   // Jordan educational
  '.edu.lb',   // Lebanon educational
  '.edu.eg',   // Egypt educational

  // School district patterns (US)
  'schools.org',
  'k12.ca.us',
  'k12.fl.us',
  'k12.tx.us',
  'k12.ny.us',
  'k12.oh.us',
]);

// Known school district domains and patterns
const SCHOOL_DISTRICT_PATTERNS = [
  // Common patterns
  /\.k12\./,
  /schools?\./,
  /school-?district/,
  /isd\./,         // Independent School District
  /usd\./,         // Unified School District
  /csd\./,         // Community School District
  /ccsd\./,        // Clark County School District (and similar)
  /education\./,
  /academy\./,
  /charter\./,

  // Middle East & GCC patterns
  /madrasah?\./,   // Islamic schools
  /madrasa\./,     // Islamic schools (alternative spelling)
  /\.gov\.qa$/,    // Qatar government institutions (include schools)
  /\.gov\.ae$/,    // UAE government institutions
  /\.gov\.sa$/,    // Saudi government institutions
  /\.gov\.kw$/,    // Kuwait government institutions
  /\.gov\.bh$/,    // Bahrain government institutions
  /\.gov\.om$/,    // Oman government institutions

  // International and private school patterns (broader detection)
  /international.*school/i, // International schools
  /american.*school/i,      // American schools
  /british.*school/i,       // British schools
  /french.*school/i,        // French schools
  /german.*school/i,        // German schools
  /indian.*school/i,        // Indian schools
  /pakistani.*school/i,     // Pakistani schools
  /filipino.*school/i,      // Filipino schools
  /\.school$/i,             // Ends with .school
  /school.*\.com$/i,        // School domains ending in .com
  /school.*\.org$/i,        // School domains ending in .org
  /academy.*\.com$/i,       // Academy domains
  /college.*\.com$/i,       // College domains
  /institute.*\.com$/i,     // Institute domains

  // State-specific patterns
  /\.k12\.ca\.us$/,
  /\.k12\.fl\.us$/,
  /\.k12\.tx\.us$/,
  /\.k12\.ny\.us$/,
  /\.k12\.oh\.us$/,
  /\.k12\.il\.us$/,
  /\.k12\.pa\.us$/,
];

// Major known educational domains (curated list)
const KNOWN_EDUCATIONAL_DOMAINS = new Set([
  // Major US universities
  'harvard.edu',
  'mit.edu',
  'stanford.edu',
  'berkeley.edu',
  'ucla.edu',
  'yale.edu',
  'princeton.edu',
  'columbia.edu',
  'upenn.edu',
  'cornell.edu',

  // Major school districts
  'lausd.net',          // Los Angeles Unified
  'nyc.gov',            // NYC Department of Education
  'cps.edu',            // Chicago Public Schools
  'browardschools.com', // Broward County
  'dadeschools.net',    // Miami-Dade
  'houstonisd.org',     // Houston ISD
  'philasd.org',        // Philadelphia
  'sps.org',            // Seattle Public Schools

  // International examples
  'ox.ac.uk',           // Oxford
  'cam.ac.uk',          // Cambridge
  'imperial.ac.uk',     // Imperial College London
  'sydney.edu.au',      // University of Sydney
  'ubc.ca',             // University of British Columbia
  'utoronto.ca',        // University of Toronto

  // Middle East & GCC institutions
  'qu.edu.qa',          // Qatar University
  'hbku.edu.qa',        // Hamad Bin Khalifa University
  'cmu.edu.qa',         // Carnegie Mellon Qatar
  'northwestern.edu.qa', // Northwestern University in Qatar
  'tamu.edu.qa',        // Texas A&M University at Qatar
  'vcu.edu.qa',         // Virginia Commonwealth University Qatar
  'ucl.ac.uk.qa',       // UCL Qatar (archived but still used)
  'aub.edu.lb',         // American University of Beirut
  'aus.edu',            // American University of Sharjah
  'uaeu.ac.ae',         // UAE University
  'adu.ac.ae',          // Abu Dhabi University
  'kaust.edu.sa',       // King Abdullah University Saudi Arabia
  'ksu.edu.sa',         // King Saud University
  'kfupm.edu.sa',       // King Fahd University
  'agu.edu.bh',         // Arabian Gulf University Bahrain
  'squ.edu.om',         // Sultan Qaboos University Oman
  'ku.edu.kw',          // Kuwait University
  'gust.edu.kw',        // Gulf University for Science & Technology

  // GCC K-12 Schools and Educational Authorities
  'moe.gov.qa',         // Ministry of Education Qatar
  'moe.gov.ae',         // Ministry of Education UAE
  'moe.gov.sa',         // Ministry of Education Saudi Arabia
  'moe.edu.kw',         // Ministry of Education Kuwait
  'moe.gov.bh',         // Ministry of Education Bahrain
  'moe.gov.om',         // Ministry of Education Oman

  // Major international schools in GCC
  'dohabritishschool.com',     // Doha British School
  'americanschoolqatar.com',   // American School of Qatar
  'sherborne-qatar.com',       // Sherborne Qatar
  'gem-wellington-doha.com',   // GEMS Wellington International School
  'disdoha.com',              // Doha International School
  'qis.org',                  // Qatar International School
  'nordangliaeducation.com',  // Nord Anglia schools
  'gems-education.com',       // GEMS Education schools

  // Major private school networks/chains in GCC
  'sabis.net',                // SABIS International Schools
  'choueifat.com',           // SABIS-ICG schools
  'ics-schools.com',         // International Community Schools
  'bisad.ae',                // British International School Abu Dhabi
  'britishschool.ae',        // British schools in UAE
  'raha.org',                // Raha International School
  'diyafatschool.com',       // Diyafat International School
  'horizonschool.ae',        // Horizon schools
  'desschools.org',          // Dubai English Speaking School
  'jess.sch.ae',            // JESS schools (Jumeira English Speaking School)
  'repton.ae',              // Repton School Dubai/Abu Dhabi
  'rak.ae',                 // Various RAK schools
  'kingschool.com.sa',      // King's Schools Saudi
  'alsalam-school.com',     // Al Salam schools
  'almanar-school.com',     // Al Manar schools
]);

/**
 * Check if an email domain is from an educational institution
 * @param {string} email - The email address to check
 * @returns {object} - Validation result with confidence level
 */
export function validateSchoolEmail(email) {
  if (!email || typeof email !== 'string') {
    return {
      isSchoolEmail: false,
      confidence: 'none',
      reason: 'Invalid email format'
    };
  }

  const emailLower = email.toLowerCase().trim();
  const domain = emailLower.split('@')[1];

  if (!domain) {
    return {
      isSchoolEmail: false,
      confidence: 'none',
      reason: 'No domain found'
    };
  }

  // Check against known educational domains (highest confidence)
  if (KNOWN_EDUCATIONAL_DOMAINS.has(domain)) {
    return {
      isSchoolEmail: true,
      confidence: 'high',
      reason: 'Known educational institution',
      domain
    };
  }

  // Check against educational TLD patterns (high confidence)
  for (const eduDomain of EDUCATIONAL_DOMAINS) {
    if (domain.endsWith(eduDomain)) {
      return {
        isSchoolEmail: true,
        confidence: 'high',
        reason: `Educational domain (${eduDomain})`,
        domain
      };
    }
  }

  // Check against school district patterns (medium confidence)
  for (const pattern of SCHOOL_DISTRICT_PATTERNS) {
    if (pattern.test(domain)) {
      return {
        isSchoolEmail: true,
        confidence: 'medium',
        reason: 'School district pattern detected',
        domain
      };
    }
  }

  // Additional heuristics (low confidence)
  const domainWords = domain.split('.');
  const domainFull = domain.toLowerCase();

  // Educational keywords with broader detection
  const educationalKeywords = [
    // Standard educational terms
    'school', 'edu', 'academy', 'university', 'college', 'institute',

    // Private school types common in GCC
    'international', 'american', 'british', 'french', 'german', 'indian',
    'pakistani', 'filipino', 'canadian', 'australian',

    // Islamic/Arabic educational terms
    'madrasa', 'madrasah', 'islamic', 'quran', 'arabic',

    // Other educational terms
    'learning', 'education', 'teaching', 'kindergarten', 'nursery',
    'primary', 'secondary', 'preparatory', 'grammar', 'high',

    // GCC specific terms
    'doha', 'qatar', 'dubai', 'sharjah', 'abudhabi', 'kuwait', 'riyadh',
    'jeddah', 'manama', 'muscat', 'bahrain', 'oman', 'emirates'
  ];

  for (const keyword of educationalKeywords) {
    if (domainFull.includes(keyword)) {
      return {
        isSchoolEmail: true,
        confidence: keyword.length > 6 ? 'medium' : 'low', // Longer/specific keywords get higher confidence
        reason: `Educational keyword detected: ${keyword}`,
        domain
      };
    }
  }

  return {
    isSchoolEmail: false,
    confidence: 'none',
    reason: 'No educational indicators found',
    domain
  };
}

/**
 * Get trial configuration based on email validation
 * @param {string} email - The email address
 * @returns {object} - Trial configuration
 */
export function getTrialConfig(email) {
  const validation = validateSchoolEmail(email);

  if (validation.isSchoolEmail && validation.confidence === 'high') {
    return {
      requiresCreditCard: false,
      trialDays: 14,
      autoApproved: true,
      requiresManualReview: false,
      reason: 'Educational email - full trial access'
    };
  }

  if (validation.isSchoolEmail && validation.confidence === 'medium') {
    return {
      requiresCreditCard: false,
      trialDays: 14,
      autoApproved: true,
      requiresManualReview: false,
      reason: 'School district email - full trial access'
    };
  }

  if (validation.isSchoolEmail && validation.confidence === 'low') {
    return {
      requiresCreditCard: true,  // Changed: Now requires card like personal emails
      trialDays: 14,
      autoApproved: false,
      requiresManualReview: false,  // Changed: No blocking, just notify admin
      notifyAdmin: true,  // New: Flag to notify admin about new school domain
      reason: 'Unknown school email - card required, admin will review domain for whitelist'
    };
  }

  return {
    requiresCreditCard: true,
    trialDays: 14,
    autoApproved: false,
    requiresManualReview: false,
    reason: 'Personal email - credit card required for trial'
  };
}

/**
 * Check if domain should be manually reviewed
 * @param {string} email - The email address
 * @returns {boolean} - Whether manual review is needed
 */
export function requiresManualReview(email) {
  const validation = validateSchoolEmail(email);
  return validation.confidence === 'low';
}

/**
 * Special handling for potential private schools that might use generic domains
 * This helps catch private schools using .com/.org domains that might be legitimate
 * @param {string} email - The email address
 * @returns {object} - Enhanced validation with private school detection
 */
export function validatePrivateSchoolEmail(email) {
  const baseValidation = validateSchoolEmail(email);

  // If already detected as school, return as-is
  if (baseValidation.isSchoolEmail) {
    return baseValidation;
  }

  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return baseValidation;

  // Check for private school indicators in domain name
  const privateSchoolIndicators = [
    // Common private school naming patterns
    /.*school.*/,
    /.*academy.*/,
    /.*college.*/,
    /.*institute.*/,
    /.*education.*/,
    /.*learning.*/,

    // Curriculum types common in private schools
    /.*ib.*/,      // International Baccalaureate
    /.*montessori.*/,
    /.*waldorf.*/,
    /.*steiner.*/,

    // Religious school indicators
    /.*christian.*/,
    /.*islamic.*/,
    /.*catholic.*/,
    /.*muslim.*/,
  ];

  for (const pattern of privateSchoolIndicators) {
    if (pattern.test(domain)) {
      return {
        isSchoolEmail: true,
        confidence: 'medium',
        reason: 'Private school pattern detected',
        domain,
        requiresVerification: true // Flag for potential manual review
      };
    }
  }

  return baseValidation;
}

// Export for testing and admin tools
export { EDUCATIONAL_DOMAINS, KNOWN_EDUCATIONAL_DOMAINS, SCHOOL_DISTRICT_PATTERNS };